import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { normalizeFormPackagesResponse } from '../adapters/formsAdapters';
import { formsApi } from '../api/formsApi';
import type { FormPackageWrite, FormThemeRecord } from '../api/types';

export function useFormPackages(enabled = true) {
  return useQuery({
    queryKey: keys.forms.packages,
    queryFn: async ({ signal }) => {
      const data = await formsApi.getPackages({ signal });
      return { packages: normalizeFormPackagesResponse(data) };
    },
    staleTime: 30_000,
    enabled,
  });
}

export function useCreateFormPackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormPackageWrite) => formsApi.createPackage(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.packages });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    },
  });
}

export function useUpdateFormPackage(packageId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<FormPackageWrite>) => formsApi.patchPackage(packageId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.packages });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    },
  });
}

export function useDeleteFormPackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (packageId: string) => formsApi.deletePackage(packageId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.packages });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    },
  });
}

export function useFormThemePresets() {
  return useQuery({
    queryKey: keys.forms.themePresets,
    queryFn: ({ signal }) => formsApi.getThemePresets({ signal }),
    staleTime: Infinity,
  });
}

export function useFormThemes() {
  return useQuery({
    queryKey: keys.forms.themes,
    queryFn: ({ signal }) => formsApi.getThemes({ signal }),
    staleTime: 120_000,
  });
}

export function useCreateFormTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<FormThemeRecord>) => formsApi.createTheme(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.themes });
    },
  });
}

export function useUpdateFormTheme(themeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<FormThemeRecord>) => formsApi.patchTheme(themeId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.themes });
    },
  });
}

export function useDeleteFormTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (themeId: string) => formsApi.deleteTheme(themeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.themes });
    },
  });
}
